import { ActionModal, ComponentBaseProps, DialogPosition } from '@ecdlink/ui';
import { GoogleMap } from '@capacitor/google-maps';
import {
  useRef,
  useLayoutEffect,
  MutableRefObject,
  useCallback,
  memo,
  useState,
  useEffect,
} from 'react';
import { useDialog } from '@ecdlink/core';

export interface Address {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GeocodeResult {
  address_components: Address[];
  types: string[];
}

// Fallback used when the device location cannot be determined.
const DEFAULT_LONGITUDE = 26.058415;
const DEFAULT_LATITUDE = -29.1199066;
const GEOLOCATION_TIMEOUT = 5000;
const MAX_LOCATION_RETRIES = 5;

const MAP_API_KEY =
  process.env.REACT_APP_MAP_API_KEY ||
  'AIzaSyAmTVxElyncQJh2hJ1ATFS0K_cB6d3VoSk';

// Create React App only inlines REACT_APP_* variables, so the original
// DEFAULT_LOCATION was always undefined and the map started at 0,0. Read the
// prefixed name, keeping the old one for continuity.
const CONFIGURED_LOCATION =
  process.env.REACT_APP_DEFAULT_LOCATION || process.env.DEFAULT_LOCATION || '';

// Each mounted map needs its own id, otherwise a later mount reuses the
// previous map's id and the stale drag listener keeps firing.
let mapInstanceCount = 0;

enum GEOLOCATIONPOSITIONERRORCODES {
  PERMISSION_DENIED = 1,
  POSITION_UNAVAILABLE = 2,
  TIMEOUT = 3,
}

const getConfiguredLocation = () => {
  const parts = CONFIGURED_LOCATION.split(',');
  const latitude = parts.length === 2 ? parseFloat(parts[0]) : NaN;
  const longitude = parts.length === 2 ? parseFloat(parts[1]) : NaN;

  return {
    latitude: isNaN(latitude) ? DEFAULT_LATITUDE : latitude,
    longitude: isNaN(longitude) ? DEFAULT_LONGITUDE : longitude,
  };
};

// The browser key is referer restricted so the Maps JavaScript API accepts it,
// but the Geocoding *web service* rejects referer restricted keys outright
// ("API keys with referer restrictions cannot be used with this API"), which
// left every reverse geocode returning REQUEST_DENIED. The JavaScript geocoder
// is loaded alongside the map and works with the same key, so use it first and
// only fall back to the REST endpoint (which needs an unrestricted key).
const geocodeWithMapsJs = (
  lat: number,
  lng: number
): Promise<GeocodeResult[] | undefined> => {
  const maps = (window as any)?.google?.maps;
  if (!maps?.Geocoder) {
    return Promise.resolve(undefined);
  }

  return new Promise((resolve) => {
    new maps.Geocoder().geocode(
      { location: { lat, lng } },
      (results: GeocodeResult[] | null, status: string) => {
        resolve(status === 'OK' && results?.length ? results : undefined);
      }
    );
  });
};

const geocodeWithRestApi = (
  lat: number,
  lng: number
): Promise<GeocodeResult[] | undefined> => {
  const params = new URLSearchParams({
    latlng: `${lat},${lng}`,
    key: MAP_API_KEY,
  });

  return fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params}`)
    .then((response) => response.json())
    .then((data) =>
      data?.status === 'OK' && data?.results?.length
        ? (data.results as GeocodeResult[])
        : undefined
    )
    .catch(() => undefined);
};

const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<Address[] | undefined> => {
  const results =
    (await geocodeWithMapsJs(lat, lng)) ?? (await geocodeWithRestApi(lat, lng));

  if (!results?.length) {
    return undefined;
  }

  // Flatten every result rather than picking one: results[0] is not always
  // street level, so the street_number/route can sit in a later result.
  return results.flatMap((result) => result.address_components ?? []);
};

function useGoogleMap(ref: MutableRefObject<HTMLElement> | any) {
  const configuredLocation = getConfiguredLocation();
  const [longitude, setLongitude] = useState(configuredLocation.longitude);
  const [latitude, setLatitude] = useState(configuredLocation.latitude);
  const [address, setAddress] = useState<Address[] | undefined>();
  const [initialCoordsSet, setInitialCoordsSet] = useState(false);
  const [locationRetries, setLocationRetries] = useState(0);

  const mapInstanceRef = useRef<GoogleMap | null>(null);
  const mapIdRef = useRef(`google-map-${(mapInstanceCount += 1)}`);
  const isMountedRef = useRef(true);

  const dialog = useDialog();
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;

  const alert = useCallback((message: string) => {
    dialogRef.current({
      position: DialogPosition.Middle,
      render: (onClose: () => void) => (
        <ActionModal
          icon={'ExclamationCircleIcon'}
          iconColor={'alertMain'}
          iconBorderColor="alertBg"
          importantText={message}
          actionButtons={[
            {
              colour: 'primary',
              text: 'Close',
              onClick: onClose,
              textColour: 'white',
              type: 'filled',
              leadingIcon: 'XIcon',
            },
          ]}
        />
      ),
    });
  }, []);

  const updateAddress = useCallback(async (lat?: number, lng?: number) => {
    if (
      lat === undefined ||
      lng === undefined ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) {
      return;
    }

    try {
      const components = await reverseGeocode(lat, lng);
      if (!isMountedRef.current || !components?.length) return;
      setAddress(components);
    } catch (error) {
      console.warn('Failed to reverse geocode map pin:', error);
    }
  }, []);

  const initMaps = async () => {
    // Created once. The previous version rebuilt the map whenever the
    // coordinates changed, stacking a new map and marker on every pin drag.
    if (mapInstanceRef.current) return;
    if (!ref.current) return;

    const map: GoogleMap = await GoogleMap.create({
      id: mapIdRef.current,
      element: ref.current,
      apiKey: MAP_API_KEY,
      config: {
        center: {
          lat: latitude,
          lng: longitude,
        },
        zoom: 15,
        disableDefaultUI: true,
      },
    });
    mapInstanceRef.current = map;

    await map.setOnMarkerDragEndListener((event) => {
      setLongitude(event?.longitude);
      setLatitude(event?.latitude);
      updateAddress(event?.latitude, event?.longitude);
    });

    // The marker is draggable, so it has already moved - there is no need to
    // remove and re-add it after a drag.
    await map.addMarker({
      coordinate: {
        lat: latitude,
        lng: longitude,
      },
      draggable: true,
    });

    updateAddress(latitude, longitude);
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      const instance = mapInstanceRef.current;
      mapInstanceRef.current = null;
      instance?.destroy().catch(() => undefined);
    };
  }, []);

  useLayoutEffect(() => {
    if (initialCoordsSet) return;

    if (!navigator.geolocation) {
      setInitialCoordsSet(true);
      return;
    }

    let settled = false;
    const applyCoords = (lat: number, lng: number) => {
      if (settled) return;
      settled = true;
      setLatitude(lat);
      setLongitude(lng);
      setInitialCoordsSet(true);
    };

    // getCurrentPosition never calls back when the permission prompt is simply
    // dismissed, so always fall through to the configured default rather than
    // leaving the map uninitialised.
    const fallbackTimer = window.setTimeout(() => {
      const fallback = getConfiguredLocation();
      applyCoords(fallback.latitude, fallback.longitude);
    }, GEOLOCATION_TIMEOUT * 2);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        window.clearTimeout(fallbackTimer);
        const fallback = getConfiguredLocation();
        applyCoords(
          coords?.latitude || fallback.latitude,
          coords?.longitude || fallback.longitude
        );
      },
      ({ code, message }) => {
        window.clearTimeout(fallbackTimer);
        console.warn(`Current location: ${message}`);

        const fallback = getConfiguredLocation();
        const canRetry = locationRetries < MAX_LOCATION_RETRIES;

        if (code === GEOLOCATIONPOSITIONERRORCODES.PERMISSION_DENIED) {
          alert('Permission not granted.');
          applyCoords(fallback.latitude, fallback.longitude);
        } else if (
          code === GEOLOCATIONPOSITIONERRORCODES.POSITION_UNAVAILABLE
        ) {
          if (canRetry) {
            setLocationRetries((previous) => previous + 1);
          } else {
            alert('Unable to determine current location.');
            applyCoords(fallback.latitude, fallback.longitude);
          }
        } else if (code === GEOLOCATIONPOSITIONERRORCODES.TIMEOUT) {
          if (canRetry) {
            setLocationRetries((previous) => previous + 1);
          } else {
            alert('Unable to determine current location (timeout).');
            applyCoords(fallback.latitude, fallback.longitude);
          }
        } else {
          applyCoords(fallback.latitude, fallback.longitude);
        }
      },
      {
        maximumAge: 0,
        timeout: GEOLOCATION_TIMEOUT,
        enableHighAccuracy: true,
      }
    );

    return () => window.clearTimeout(fallbackTimer);
  }, [initialCoordsSet, locationRetries, alert]);

  useLayoutEffect(() => {
    if (!initialCoordsSet) return;
    (async () => {
      await initMaps();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoordsSet, ref]);

  return {
    address,
    coords: {
      latitude,
      longitude,
    },
    setLatitude,
    setLongitude,
  };
}

function CustomGoogleMapComponent(
  props: ComponentBaseProps & {
    children?: React.ReactNode;
    height?: number;
    onChange?: (address?: Address[]) => void;
  }
) {
  const mapRef = useRef();

  const { address } = useGoogleMap(mapRef);

  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  // Keyed on the address alone - the previous version also depended on `props`,
  // which is a new object every render, so it fired continuously.
  useEffect(() => {
    onChangeRef.current?.(address);
  }, [address]);

  return (
    <>
      <div className="component-wrapper">
        <capacitor-google-map
          ref={mapRef}
          style={{
            display: 'inline-block',
            width: window.screen.width,
            height: props.height || window.screen.height / 2,
          }}
        />
      </div>
      {props.children}
    </>
  );
}

export const CustomGoogleMap = memo(CustomGoogleMapComponent);
